/**
 * Polygon - Regular polygon (n-sided)
 */

import { GeometryType } from '../base/GeometryBase';
import { Vector } from '../base/Vector';
import { Polyline } from './Polyline';

export class Polygon extends Polyline {
  /**
   * Create a regular polygon
   * @param {Vector|{x,y,z}} center - Center point
   * @param {number} radius - Circumradius (center to vertex)
   * @param {number} sides - Number of sides (3 = triangle, 4 = square, etc.)
   * @param {number} rotation - Rotation angle in radians (default: 0)
   * @param {Object} attributes
   */
  constructor(center, radius, sides = 6, rotation = 0, attributes = {}) {
    const c = center instanceof Vector ? center : Vector.fromObject(center);

    // Minimum 3 sides
    const n = Math.max(3, Math.floor(sides));

    // Generate vertices
    const points = [];
    const angleStep = (2 * Math.PI) / n;

    for (let i = 0; i < n; i++) {
      const angle = rotation + i * angleStep;
      points.push(new Vector(
        c.x + radius * Math.cos(angle),
        c.y + radius * Math.sin(angle),
        c.z
      ));
    }

    super(points, true, attributes); // closed = true

    // Override type
    this.type = GeometryType.POLYGON || 'Polygon';
    this.id = this.id.replace('pl_', 'poly_');

    // Store parameters
    this._center = c;
    this._radius = radius;
    this._sides = n;
    this._rotation = rotation;
  }

  /**
   * Create polygon from center and point on vertex
   * @param {Vector|{x,y,z}} center
   * @param {Vector|{x,y,z}} vertex - One vertex point
   * @param {number} sides
   * @param {Object} attributes
   * @returns {Polygon}
   */
  static fromCenterAndVertex(center, vertex, sides, attributes = {}) {
    const c = center instanceof Vector ? center : Vector.fromObject(center);
    const v = vertex instanceof Vector ? vertex : Vector.fromObject(vertex);
    const radius = c.distanceTo(v);
    const rotation = Math.atan2(v.y - c.y, v.x - c.x);
    return new Polygon(c, radius, sides, rotation, attributes);
  }

  /**
   * Create polygon inscribed in circle (edge midpoints on circle)
   * @param {Vector|{x,y,z}} center
   * @param {number} inradius - Inradius (center to edge midpoint)
   * @param {number} sides
   * @param {number} rotation
   * @param {Object} attributes
   * @returns {Polygon}
   */
  static inscribed(center, inradius, sides, rotation = 0, attributes = {}) {
    // Convert inradius to circumradius
    const circumradius = inradius / Math.cos(Math.PI / sides);
    return new Polygon(center, circumradius, sides, rotation, attributes);
  }

  get center() {
    return this._center.clone();
  }

  get radius() {
    return this._radius;
  }

  get circumradius() {
    return this._radius;
  }

  get inradius() {
    return this._radius * Math.cos(Math.PI / this._sides);
  }

  get sides() {
    return this._sides;
  }

  get rotation() {
    return this._rotation;
  }

  get sideLength() {
    return 2 * this._radius * Math.sin(Math.PI / this._sides);
  }

  get area() {
    // Area = (1/2) * n * r² * sin(2π/n)
    return (this._sides * this._radius * this._radius * Math.sin(2 * Math.PI / this._sides)) / 2;
  }

  get perimeter() {
    return this._sides * this.sideLength;
  }

  /**
   * Get interior angle
   * @returns {number} Interior angle in radians
   */
  get interiorAngle() {
    return ((this._sides - 2) * Math.PI) / this._sides;
  }

  /**
   * Get exterior angle
   * @returns {number} Exterior angle in radians
   */
  get exteriorAngle() {
    return (2 * Math.PI) / this._sides;
  }

  clone() {
    return new Polygon(
      this._center.clone(),
      this._radius,
      this._sides,
      this._rotation,
      { ...this.attributes }
    );
  }

  transform({ translate, rotate, scale, mirror }) {
    let center = this._center.clone();
    let radius = this._radius;
    let rotation = this._rotation;

    if (scale) {
      const s = typeof scale === 'number' ? scale : 1;
      center = center.multiply(s);
      radius *= s;
    }

    if (rotate) {
      center = center.rotate2D(rotate);
      rotation += rotate;
    }

    if (translate) {
      center = center.add(Vector.fromObject(translate));
    }

    if (mirror) {
      center = center.mirror(Vector.fromObject(mirror));
      rotation = -rotation; // Mirror flips rotation
    }

    return new Polygon(center, radius, this._sides, rotation, { ...this.attributes });
  }

  toJSON() {
    return {
      ...super.toJSON(),
      type: 'Polygon',
      center: this._center.toObject(),
      radius: this._radius,
      sides: this._sides,
      rotation: this._rotation,
    };
  }

  static fromJSON(json) {
    const poly = new Polygon(
      Vector.fromObject(json.center),
      json.radius,
      json.sides,
      json.rotation,
      json.attributes
    );
    poly.id = json.id;
    poly.createdAt = json.createdAt;
    poly.modifiedAt = json.modifiedAt;
    return poly;
  }
}

export default Polygon;
